import React, { useEffect } from "react"
import Head from "next/head"
import AemGrid from "@/components/AemGrid"
import { authorBaseUrl, footerUrl, headerUrl } from "@/constants"
import { aemAxiosInstance } from "@/constants/api"
import { useDispatch } from "react-redux"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { i18n } from "next-i18next"

import { filterBackEndJson, getClienIP, getPathFromUrl } from "@/utils/helper"

import { setGbhDataLayerCustom } from "@/store/features/genericSlice"
import usePageAuth from "@/hooks/usePageAuth"
import { getDataLayerObj } from "@/utils/helper"
import GlobalPageHeader from "@/components/Shared/GlobalPage/GlobalPageHeader"
import GlobalPageFooter from "@/components/Shared/GlobalPage/GlobalPageFooter"

/**
 * renders home page
 * @return {JSX.Element}
 */
function ProductRegistration({
  tmpData: data,
  headertmp: header,
  footertmp: footer,
  ip,
  globalScript,
  footerScript,
  analyticsData,
  gbhDataLayerCustom,
}) {
  const dispatch = useDispatch()

  useEffect(() => {
    if (ip) sessionStorage.setItem("true-ip", ip)
  }, [ip])

  usePageAuth()

  useEffect(() => {
    getDataLayerObj(analyticsData)
  }, [analyticsData])

  useEffect(() => {
    dispatch(setGbhDataLayerCustom(gbhDataLayerCustom))
    return () => dispatch(setGbhDataLayerCustom(""))
  }, [])

  return (
    <>
      <Head>
        <title>{data?.title}</title>
        <meta name="description" content={data?.description} />
        <meta property="og:title" content={data?.title} />
        <meta property="og:description" content={data?.description} />
        <meta property="og:url" content="/en" />
        <meta property="og:type" content={data?.templateName} />
      </Head>
      <>
        <GlobalPageHeader
          header={header}
          title={data.title}
          globalScripting={globalScript}
        />
        <AemGrid data={data} count={0} />
        <GlobalPageFooter footer={footer} footerScripting={footerScript} />
      </>
    </>
  )
}

/**
 *
 * @return {Object}
 */
export async function getServerSideProps({ req, res, locale }) {
  await i18n?.reloadResources()
  const lang = locale ? `/${locale}/` : ""
  const path = `${getPathFromUrl(req.url)}.model.json`
  const urlPath = req.url.split("?")[0]
  const ip = getClienIP(req)
  const { data } = await aemAxiosInstance({
    url: `${authorBaseUrl}${lang}${path}`,
    method: "GET",
  })

  const { data: header } = await aemAxiosInstance({
    url: `${data.headerExpFrg ? data.headerExpFrg : headerUrl}.model.json`,
    method: "GET",
  })

  const { data: footer } = await aemAxiosInstance({
    url: `${data.footerExpFrg ? data.footerExpFrg : footerUrl}.model.json`,
    method: "GET",
  })

  const globalScript = data?.globalScript ? data?.globalScript : null
  const footerScript = data?.footerScript ? data?.footerScript : null
  const topHeadScript = data?.topHeadScript ? data?.topHeadScript : null
  const analyticsScriptClass = data?.analyticsScriptClass
    ? data?.analyticsScriptClass
    : null

  let tmpData = {}
  if (data.pageComponentProperties) {
    tmpData = data.pageComponentProperties
  } else {
    tmpData = data
  }

  const analyticsData = data?.data ? data?.data : null

  const gbhDataLayerCustom = {}
  if (data?.datalayerPageName) {
    gbhDataLayerCustom.datalayerPageName = data?.datalayerPageName
  }
  if (data?.eventPageType) {
    gbhDataLayerCustom.eventPageType = data?.eventPageType
  }
  if (data?.pageType) {
    gbhDataLayerCustom.pageType = data?.pageType
  }
  tmpData = filterBackEndJson(tmpData)
  const headertmp = filterBackEndJson(header)
  const footertmp = filterBackEndJson(footer)
  const metaContent = data?.metaContent ?? ""
  const canonicalUrl = data?.canonicalUrl ?? ""
  // Pass data to the page via props
  return {
    props: {
      ip,
      tmpData,
      headertmp,
      footertmp,
      globalScript,
      footerScript,
      topHeadScript,
      analyticsData,
      analyticsScriptClass,
      gbhDataLayerCustom,
      urlPath,
      metaContent,
      canonicalUrl,
      ...(await serverSideTranslations(locale, ["common"])),
    },
  }
}

export default ProductRegistration
